

import { IconButton, Alert, ConfirmDialog } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useRestoreStoreSubscriptionPaymentMutation } from "@/modules/stores/services/mutations/useRestoreStoreSubscriptionPaymentMutation";

type Props = {
  storeId: string;
  paymentId: string;
};

export function SubscriptionPaymentRestoreButton({ storeId, paymentId }: Props) {
  const t = useTranslations();
  const { mutateAsync: restorePayment } = useRestoreStoreSubscriptionPaymentMutation();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleRestore = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await restorePayment({ storeId, paymentId });
    } catch (caughtError) {
      if (caughtError instanceof ApiJsonClientError) {
        setError(caughtError.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col gap-2">
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      <IconButton
        type="button"
        onClick={() => setIsDialogOpen(true)}
        disabled={isSubmitting}
        ariaLabel={t(keys.admin.subscriptionPaymentRestoreAction)}
        variant="success"
        size="md"
        icon={
          isSubmitting ? (
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 animate-spin"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="9" className="opacity-25" />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                className="opacity-75"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="currentColor"
            >
              <path d="M12 5a7 7 0 1 0 7 7h-2a5 5 0 1 1-5-5h2.2L12 9.2 9.8 7H12Z" />
            </svg>
          )
        }
      />
      <ConfirmDialog
        isOpen={isDialogOpen}
        title={t(keys.admin.subscriptionPaymentRestoreConfirmTitle)}
        body={t(keys.admin.subscriptionPaymentRestoreConfirmBody)}
        confirmLabel={t(keys.admin.subscriptionPaymentRestoreAction)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSubmitting}
        onConfirm={() => {
          setIsDialogOpen(false);
          handleRestore();
        }}
        onCancel={() => setIsDialogOpen(false)}
      />
    </div>
  );
}
